import React, { useState, useEffect, useRef } from 'react';
import { Card, Progress, Table, Tag, Button, Space, Statistic, Row, Col, Timeline, Alert, Spin } from 'antd';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { JobStatus, ConnectorJob, JobStatusResponse, ProcessingResult } from '../types/connector';
import { connectorApi } from '../services/connectorApi';

interface JobMonitorProps {
  jobId: string;
  refreshInterval?: number; // in seconds
  onJobComplete?: (jobId: string, success: boolean) => void;
}

interface JobProgress {
  stage: string;
  documents_found: number;
  documents_processed: number;
  documents_failed: number;
  current_batch: number;
  total_batches: number;
  processing_time: number;
  success_rate: number;
}

const JobMonitor: React.FC<JobMonitorProps> = ({ 
  jobId, 
  refreshInterval = 5,
  onJobComplete 
}) => {
  const [job, setJob] = useState<ConnectorJob | null>(null);
  const [status, setStatus] = useState<JobStatusResponse | null>(null);
  const [results, setResults] = useState<ProcessingResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch job details
  const fetchJobDetails = async () => {
    try {
      const jobDetails = await connectorApi.getConnector(jobId);
      setJob(jobDetails);
    } catch (err) {
      setError('Failed to fetch job details');
      console.error(err);
    }
  };

  // Fetch job status and progress
  const fetchJobStatus = async () => {
    try {
      const statusResponse = await connectorApi.getJobStatus(jobId);
      setStatus(statusResponse);
      
      // Check if job completed
      if (statusResponse.current_status === JobStatus.COMPLETED || 
          statusResponse.current_status === JobStatus.FAILED) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        
        if (onJobComplete) {
          onJobComplete(jobId, statusResponse.current_status === JobStatus.COMPLETED);
        }
      }
    } catch (err) {
      console.error('Failed to fetch job status:', err);
    }
  };

  // Fetch job results
  const fetchJobResults = async () => {
    try {
      const resultsResponse = await connectorApi.getJobResults(jobId, undefined, 50);
      setResults(resultsResponse.results || []);
    } catch (err) {
      console.error('Failed to fetch job results:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchJobDetails(),
        fetchJobStatus(),
        fetchJobResults()
      ]);
      setLoading(false);
    };

    loadData();
  }, [jobId]);

  // Set up refresh interval
  useEffect(() => {
    if (status?.current_status === JobStatus.RUNNING || status?.current_status === JobStatus.QUEUED) {
      intervalRef.current = setInterval(() => {
        fetchJobStatus();
        fetchJobResults();
      }, refreshInterval * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [status?.current_status, refreshInterval]);

  const handleJobAction = async (action: 'start' | 'pause') => {
    try {
      if (action === 'start') {
        await connectorApi.startJob(jobId);
      } else {
        await connectorApi.pauseJob(jobId);
      }
      
      // Refresh status after action
      setTimeout(fetchJobStatus, 1000);
    } catch (err) {
      console.error(`Failed to ${action} job:`, err);
    }
  };

  const getStatusColor = (status: JobStatus) => {
    const colorMap = {
      [JobStatus.PENDING]: 'default',
      [JobStatus.QUEUED]: 'blue',
      [JobStatus.RUNNING]: 'processing',
      [JobStatus.COMPLETED]: 'success',
      [JobStatus.FAILED]: 'error',
      [JobStatus.CANCELLED]: 'default',
      [JobStatus.PAUSED]: 'warning'
    };
    return colorMap[status] || 'default';
  };

  const getProgressPercentage = (): number => {
    if (!status?.progress) return 0;
    
    const { documents_found, documents_processed, documents_failed } = status.progress;
    const total = documents_found || 1;
    const completed = (documents_processed || 0) + (documents_failed || 0);
    
    return Math.round((completed / total) * 100);
  };

  const getProcessingStage = (): string => {
    if (!status?.progress?.stage) return 'Initializing';
    
    const stageMap: Record<string, string> = {
      'discovery': 'Discovering documents',
      'queuing': 'Queuing for processing', 
      'processing': 'Processing documents',
      'completed': 'Processing completed'
    };
    
    return stageMap[status.progress.stage] || status.progress.stage;
  };

  const getTimelineItems = () => {
    const items = [];
    
    if (job?.created_at) {
      items.push({
        dot: <ClockCircleOutlined />,
        children: (
          <div>
            <div>Job Created</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {new Date(job.created_at).toLocaleString()}
            </div>
          </div>
        )
      });
    }
    
    if (status?.current_run?.started_at) {
      items.push({
        dot: <PlayCircleOutlined />,
        children: (
          <div>
            <div>Execution Started</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {new Date(status.current_run.started_at).toLocaleString()}
            </div>
          </div>
        )
      });
    }
    
    if (status?.current_status === JobStatus.RUNNING && status.progress) {
      items.push({
        dot: <Spin size="small" />,
        children: (
          <div>
            <div>{getProcessingStage()}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {status.progress.documents_processed || 0} of {status.progress.documents_found || 0} documents processed
            </div>
          </div>
        )
      });
    }
    
    if (status?.current_status === JobStatus.COMPLETED) {
      items.push({
        dot: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        children: (
          <div>
            <div>Job Completed Successfully</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Processed {status.progress?.documents_processed || 0} documents
            </div>
          </div>
        )
      });
    }
    
    if (status?.current_status === JobStatus.FAILED) {
      items.push({
        dot: <ExclamationCircleOutlined style={{ color: '#f5222d' }} />,
        children: (
          <div>
            <div>Job Failed</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Check logs for error details
            </div>
          </div>
        )
      });
    }
    
    return items;
  };

  // Results table columns
  const resultColumns: ColumnsType<ProcessingResult> = [
    {
      title: 'Document',
      dataIndex: 'source_path',
      key: 'source_path',
      render: (path: string) => {
        const fileName = path.split('/').pop() || path;
        return (
          <div>
            <FileTextOutlined style={{ marginRight: 8 }} />
            {fileName}
          </div>
        );
      }
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      render: (classification: string, record: ProcessingResult) => (
        <div>
          <Tag color={record.error ? 'error' : 'success'}>
            {classification || 'Failed'}
          </Tag>
          {record.confidence && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Confidence: {(record.confidence * 100).toFixed(1)}%
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Processing Time',
      dataIndex: 'processing_time',
      key: 'processing_time',
      width: 120,
      render: (time: number) => `${(time || 0).toFixed(2)}s`
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (record: ProcessingResult) => (
        <Tag color={record.error ? 'error' : 'success'}>
          {record.error ? 'Failed' : 'Success'}
        </Tag>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 64 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading job details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Job"
        description={error}
        type="error"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>{job?.name}</h2>
          <div style={{ color: '#666' }}>
            {job?.connector_type?.toUpperCase()} • {job?.capability}
          </div>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchJobStatus();
              fetchJobResults();
            }}
          >
            Refresh
          </Button>
          {status?.current_status === JobStatus.PENDING && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => handleJobAction('start')}
            >
              Start Job
            </Button>
          )}
          {(status?.current_status === JobStatus.RUNNING || status?.current_status === JobStatus.QUEUED) && (
            <Button
              icon={<PauseCircleOutlined />}
              onClick={() => handleJobAction('pause')}
            >
              Pause Job
            </Button>
          )}
        </Space>
      </div>

      {/* Status Overview */}
      <Card title="Job Status" style={{ marginBottom: 24 }}>
        <Row gutter={24} align="middle">
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Tag 
                color={getStatusColor(status?.current_status || JobStatus.PENDING)} 
                style={{ fontSize: 16, padding: '8px 16px' }}
              >
                {status?.current_status?.toUpperCase()}
              </Tag>
            </div>
          </Col>
          <Col span={18}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>{getProcessingStage()}</span>
                <span>{getProgressPercentage()}% Complete</span>
              </div>
              <Progress 
                percent={getProgressPercentage()}
                status={status?.current_status === JobStatus.FAILED ? 'exception' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068'
                }}
              />
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={24} style={{ marginBottom: 24 }}>
        {/* Processing Statistics */}
        <Col span={12}>
          <Card title="Processing Statistics">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Found"
                  value={status?.progress?.documents_found || 0}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Processed"
                  value={status?.progress?.documents_processed || 0}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Failed"
                  value={status?.progress?.documents_failed || 0}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Col>
            </Row>
            {status?.progress?.processing_time && (
              <div style={{ marginTop: 16 }}>
                <Statistic
                  title="Processing Time"
                  value={status.progress.processing_time}
                  suffix="seconds"
                  precision={2}
                />
              </div>
            )}
          </Card>
        </Col>

        {/* Job Timeline */}
        <Col span={12}>
          <Card title="Job Timeline">
            <Timeline items={getTimelineItems()} />
          </Card>
        </Col>
      </Row>

      {/* Processing Results */}
      <Card title="Processing Results">
        <Table
          columns={resultColumns}
          dataSource={results}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `${total} results`
          }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default JobMonitor;